/**
 * Desktop product-carousel scroll indicator.
 * Maps slideshow-slides scrollLeft to a moving thumb on a short track.
 */
class CarouselScrollProgress extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {HTMLElement | null} */
  #thumb = null;

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {MediaQueryList | null} */
  #mediaQuery = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {number} */
  #raf = 0;

  connectedCallback() {
    this.#thumb = this.querySelector('.carousel-scroll-progress__thumb');
    this.#track = this.querySelector('.carousel-scroll-progress__track');
    const slideshow = this.closest('slideshow-component');
    this.#scroller = slideshow?.querySelector('slideshow-slides') ?? null;

    if (!this.#thumb || !this.#track || !this.#scroller) return;

    this.#mediaQuery = window.matchMedia('(min-width: 750px)');
    this.#scroller.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#mediaQuery.addEventListener('change', this.#handleMediaChange);

    this.#resizeObserver = new ResizeObserver(() => this.#scheduleUpdate());
    this.#resizeObserver.observe(this.#scroller);

    if (slideshow) {
      this.#mutationObserver = new MutationObserver(() => this.#scheduleUpdate());
      this.#mutationObserver.observe(slideshow, {
        attributes: true,
        attributeFilter: ['in-viewport'],
      });
    }

    this.#scheduleUpdate();
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#handleScroll);
    this.#mediaQuery?.removeEventListener('change', this.#handleMediaChange);
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    if (this.#raf) cancelAnimationFrame(this.#raf);
  }

  #handleMediaChange = () => {
    this.#scheduleUpdate();
  };

  #handleScroll = () => {
    this.#scheduleUpdate();
  };

  #scheduleUpdate() {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#update();
    });
  }

  #update() {
    const scroller = this.#scroller;
    const thumb = this.#thumb;
    const track = this.#track;
    if (!scroller || !thumb || !track) return;

    if (!this.#mediaQuery?.matches) {
      this.hidden = true;
      return;
    }

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll <= 2) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    const trackWidth = track.clientWidth;
    if (trackWidth === 0) return;

    const thumbWidth = Math.min(Math.max(trackWidth * 0.22, 36), 48);
    const maxOffset = Math.max(trackWidth - thumbWidth, 0);
    const progress = Math.min(Math.max(Math.abs(scroller.scrollLeft) / maxScroll, 0), 1);

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${progress * maxOffset}px)`;
  }
}

if (!customElements.get('carousel-scroll-progress')) {
  customElements.define('carousel-scroll-progress', CarouselScrollProgress);
}
